import { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { useFornecedor, useFornecedorDetalhe } from '@/features/compras/hooks/useFornecedor';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';

const FornecedorForm = () => {
  const navigate = useNavigate();
  // A consulta passa os filtros (query string) para voltar à mesma lista.
  const voltarPara = (useLocation().state as { voltarPara?: string } | null)?.voltarPara ?? '';
  const voltar = () => navigate(`/compras/fornecedores${voltarPara}`);
  const { id } = useParams<{ id: string }>();
  const isEdicao = Boolean(id && id !== 'novo');
  const idNum = isEdicao ? Number(id) : 0;

  const { criarFornecedor, atualizarFornecedor, isCreating, isUpdating } = useFornecedor();
  const { data: detalhe } = useFornecedorDetalhe(idNum);

  const [nomeRazao,         setNomeRazao]         = useState('');
  const [apelido,           setApelido]           = useState('');
  const [cpfCnpj,           setCpfCnpj]           = useState('');
  const [inscricaoEstadual, setInscricaoEstadual] = useState('');
  const [email,             setEmail]             = useState('');
  const [fone,              setFone]              = useState('');
  const [celular,           setCelular]           = useState('');
  const [tipoPessoa,        setTipoPessoa]        = useState('J');
  const [observacao,        setObservacao]        = useState('');
  const [isAtivo,           setIsAtivo]           = useState(true);

  useEffect(() => {
    if (detalhe) {
      setNomeRazao(detalhe.nomeRazao ?? '');
      setApelido(detalhe.apelido ?? '');
      setCpfCnpj(detalhe.cpfCnpj ?? '');
      setInscricaoEstadual(detalhe.inscricaoEstadual ?? '');
      setEmail(detalhe.email ?? '');
      setFone(detalhe.fone ?? '');
      setCelular(detalhe.celular ?? '');
      setTipoPessoa(detalhe.tipoPessoa ?? 'J');
      setObservacao(detalhe.observacao ?? '');
      setIsAtivo(detalhe.isAtivo);
    }
  }, [detalhe]);

  const handleSalvar = async () => {
    if (!nomeRazao.trim()) return;

    if (isEdicao) {
      await atualizarFornecedor({
        id: idNum,
        data: { nomeRazao, apelido, cpfCnpj, inscricaoEstadual, email, fone, celular, tipoPessoa, observacao, isAtivo },
      });
    } else {
      const novo = await criarFornecedor({ nomeRazao, apelido, cpfCnpj, inscricaoEstadual, email, fone, celular, tipoPessoa, observacao });
      navigate(`/compras/fornecedores/${novo.id}`);
      return;
    }
    voltar();
  };

  const isSaving = isCreating || isUpdating;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={voltar}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{isEdicao ? 'Editar Fornecedor' : 'Novo Fornecedor'}</h1>
          <p className="text-muted-foreground text-sm">Cadastro de fornecedor</p>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle>Dados do Fornecedor</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nome / Razão Social *</Label>
              <Input value={nomeRazao} onChange={e => setNomeRazao(e.target.value)} placeholder="Nome ou Razão Social" />
            </div>
            <div className="space-y-2">
              <Label>Apelido / Nome Fantasia</Label>
              <Input value={apelido} onChange={e => setApelido(e.target.value)} placeholder="Apelido" />
            </div>
            <div className="space-y-2">
              <Label>CPF / CNPJ</Label>
              <Input value={cpfCnpj} onChange={e => setCpfCnpj(e.target.value)} placeholder="00.000.000/0001-00" />
            </div>
            <div className="space-y-2">
              <Label>Inscrição Estadual</Label>
              <Input value={inscricaoEstadual} onChange={e => setInscricaoEstadual(e.target.value)} placeholder="IE" />
            </div>
            <div className="space-y-2">
              <Label>E-mail</Label>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@fornecedor.com" />
            </div>
            <div className="space-y-2">
              <Label>Telefone</Label>
              <Input value={fone} onChange={e => setFone(e.target.value)} placeholder="(00) 0000-0000" />
            </div>
            <div className="space-y-2">
              <Label>Celular / WhatsApp</Label>
              <Input value={celular} onChange={e => setCelular(e.target.value)} placeholder="(00) 00000-0000" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Observações</Label>
            <Textarea value={observacao} onChange={e => setObservacao(e.target.value)} rows={3} />
          </div>

          {isEdicao && (
            <div className="flex items-center gap-3">
              <Switch checked={isAtivo} onCheckedChange={setIsAtivo} />
              <Label>{isAtivo ? 'Ativo' : 'Inativo'}</Label>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex gap-3">
        <Button onClick={handleSalvar} disabled={isSaving || !nomeRazao.trim()} className="gap-2">
          <Save className="h-4 w-4" />
          {isSaving ? 'Salvando...' : 'Salvar'}
        </Button>
        <Button variant="outline" onClick={voltar}>Cancelar</Button>
      </div>
    </div>
  );
};

export default FornecedorForm;
